import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';
import { RELEASE_SPORTS } from '@config/releaseProfile';
import { SPORTS } from '@utils/sports';

const PLAN_OPTIONS = [
  { id: 'academies', label: 'Academy' },
  { id: 'schools', label: 'School/College' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'coaches', label: 'Coach' },
] as const;

const SPORT_CHOICES = Array.from(
  new Set([...RELEASE_SPORTS, ...SPORTS.filter((sport) => sport !== 'Other'), 'Others']),
);

const SubscriptionContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planFromQuery = String(params.get('plan') || '').toLowerCase();
  const initialPlan = PLAN_OPTIONS.some((p) => p.id === planFromQuery) ? planFromQuery : 'academies';

  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [orgName, setOrgName] = useState('');
  const [plan, setPlan] = useState(initialPlan);
  const [studentCount, setStudentCount] = useState('');
  const [sports, setSports] = useState<string[]>([...RELEASE_SPORTS]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const planLabel = useMemo(
    () => PLAN_OPTIONS.find((p) => p.id === plan)?.label || 'Organization',
    [plan],
  );

  const toggleSport = (sport: string) => {
    setSports((prev) => (prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/sales-inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactName: contactName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          orgName: orgName.trim(),
          plan,
          studentCount: studentCount.trim(),
          sports,
          notes: notes.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit');
      }
      setSubmitted(true);
      setContactName('');
      setEmail('');
      setPhone('');
      setOrgName('');
      setStudentCount('');
      setSports([...RELEASE_SPORTS]);
      setNotes('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicContentPageShell
      title="Talk to sales"
      subtitle="Academies, schools, and corporate teams use this form for a yearly quote. Player and parent account help stays on Support."
      maxWidth="md"
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: brandColors.white,
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontFamily: '"Sora", Arial, sans-serif',
            color: brandColors.navy,
            mb: 0.5,
          }}
        >
          Pre-sales enquiry
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tell us about {planLabel.toLowerCase()} size and the sports you want to run. We reply within 1–2 business days.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {submitted && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitted(false)}>
            Thanks — your sales request was sent. Our team will follow up with a yearly quote.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Contact name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Work email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Organization name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              required
              label="Organization type"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
            >
              {PLAN_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              inputProps={{ min: 1 }}
              label="Number of students / athletes"
              value={studentCount}
              onChange={(e) => setStudentCount(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 700, color: brandColors.navy, mb: 0.5 }}>
              Sports of focus
            </Typography>
            <FormGroup row>
              {SPORT_CHOICES.map((sport) => (
                <FormControlLabel
                  key={sport}
                  control={
                    <Checkbox
                      checked={sports.includes(sport)}
                      onChange={() => toggleSport(sport)}
                      sx={{ color: brandColors.orange, '&.Mui-checked': { color: brandColors.orange } }}
                    />
                  }
                  label={sport}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Anything else we should know?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }} alignItems={{ sm: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={<SendIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: brandColors.navy,
              '&:hover': { bgcolor: '#0a0f28' },
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Sales Enquiry'}
          </Button>
          <Button onClick={() => navigate('/subscriptions')} sx={{ textTransform: 'none' }}>
            Back to subscriptions
          </Button>
        </Stack>
      </Box>
    </PublicContentPageShell>
  );
};

export default SubscriptionContactPage;
